import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData, useNavigate } from '@remix-run/react'
import { useState } from 'react'
import { Button } from '#app/components/ui/button.js'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from '#app/components/ui/carousel.tsx'
import {
	Dialog,
	DialogContent,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { cn, getPaintingImgSrc } from '#app/utils/misc.js'

export async function loader({ params }: LoaderFunctionArgs) {
	const painting = await prisma.painting.findFirst({
		select: {
			id: true,
			title: true,
			price: true,
			measures: true,
			content: true,
			images: {
				select: {
					id: true,
					altText: true,
				},
				orderBy: {
					order: 'asc',
				},
			},
		},
		where: {
			id: params.id,
		},
	})
	invariantResponse(painting, 'Owner not found', { status: 404 })

	return json({ painting })
}

export default function PaintingDetailRoute() {
	const { painting } = useLoaderData<typeof loader>()
	const [selectedImage, setSelectedImage] = useState(painting.images[0])
	const navigate = useNavigate()

	return (
		<div className="mx-10 mt-4 flex flex-col gap-8 md:ml-10 md:mr-0 md:mt-10 md:flex-row">
			<div className="flex max-w-96 flex-col gap-4 md:aspect-square">
				<Dialog>
					<DialogTrigger asChild>
						<img
							className="w-80 min-w-[200px]"
							src={getPaintingImgSrc(selectedImage?.id!)}
						/>
					</DialogTrigger>
					<DialogContent className="max-w-xl p-0">
						<Carousel>
							<CarouselContent>
								{painting?.images.map((image, i) => {
									return (
										<CarouselItem key={i}>
											<img src={getPaintingImgSrc(image.id)} />
										</CarouselItem>
									)
								})}
							</CarouselContent>
							<CarouselPrevious />
							<CarouselNext />
						</Carousel>
					</DialogContent>
				</Dialog>
				<div className="flex flex-wrap justify-start gap-2">
					{painting?.images.map((image, i) => {
						return (
							<div
								key={i}
								className={cn(
									selectedImage?.id === image.id && 'border-2 border-[#688329]',
									'aspect-square',
								)}
							>
								<img
									className="h-[56px] w-[56px] object-contain"
									src={getPaintingImgSrc(image.id)}
									width="56"
									height="56"
									onClick={() => {
										setSelectedImage(image)
									}}
								/>
							</div>
						)
					})}
				</div>
			</div>
			<div className="mb-4 mr-8 flex max-w-[700px] flex-col">
				<h1 className="text-h5 italic">{`"${painting.title}"`}</h1>

				<p className="pt-4 text-body-xs md:text-body-md">
					${painting.price}CAD
				</p>
				<p className="pt-4 text-body-xs md:text-body-md">
					<strong>Measurements: </strong>
					{painting.measures}
				</p>
				<p className="whitespace-break-spaces pt-4 text-body-xs md:text-body-md">
					{painting.content}
				</p>
				<Button
					className="mt-12 w-24 self-end bg-[#688329]"
					onClick={() => navigate(`/contact-me?q=${painting.id}`)}
				>
					Buy
				</Button>
			</div>
		</div>
	)
}
